import type { JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useClassStudents, useEnrollStudent, useRemoveStudent } from '@/features/classes/hooks';
import { useStudents } from '@/features/students/hooks';
import { ApiError } from '@/lib/api/client';
import { useHasCapability } from '@/lib/capabilities';

const ROSTER_PAGE_SIZE = 100;

/**
 * Student enrollment for one class (Phase G-6). `useStudents` (Phase G-1)
 * is reused verbatim as the enrollment picker's data source - no duplicate
 * student list is built. `StudentView`/`ClassStudentView.student` now
 * carry the account holder's display name (G-9, Job 9) - the picker and
 * the enrolled-roster list both show it, mirroring
 * `ClassTeachersSection`'s existing pattern exactly.
 */
export function ClassStudentsSection({ classId }: { readonly classId: string }): JSX.Element {
  const { data: enrollments, isLoading, isError } = useClassStudents(classId);
  const { data: roster } = useStudents(1, ROSTER_PAGE_SIZE);
  const canManage = useHasCapability('students:manage');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const enroll = useEnrollStudent(classId);
  const remove = useRemoveStudent(classId);

  const enrolledIds = new Set((enrollments ?? []).map((entry) => entry.studentId));
  const availableStudents = (roster?.items ?? []).filter((student) => !enrolledIds.has(student.id));

  function handleEnroll(): void {
    if (selectedStudentId.length === 0) {
      return;
    }
    enroll.mutate(
      { studentId: selectedStudentId },
      {
        onSuccess: () => {
          setSelectedStudentId('');
        },
      },
    );
  }

  const apiErrorMessage =
    enroll.error instanceof ApiError
      ? enroll.error.message
      : enroll.error !== null
        ? 'Gagal mendaftarkan santri. Silakan coba lagi.'
        : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Santri</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLoading && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat daftar santri.</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && enrollments !== undefined && enrollments.length === 0 && (
          <p className="text-sm text-muted-foreground">Belum ada santri yang terdaftar.</p>
        )}

        {!isLoading && !isError && enrollments !== undefined && enrollments.length > 0 && (
          <ul className="flex flex-col gap-2">
            {enrollments.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <Link
                  to={`/students/${entry.studentId}`}
                  className="flex min-w-0 flex-col hover:underline"
                >
                  <span className="truncate text-sm font-medium">{entry.student.fullName}</span>
                  {entry.student.guardianName !== null && (
                    <span className="truncate text-xs text-muted-foreground">
                      Wali: {entry.student.guardianName}
                    </span>
                  )}
                </Link>
                {canManage && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button type="button" variant="outline" size="sm" disabled={remove.isPending}>
                        Keluarkan
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Keluarkan santri ini dari kelas?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Santri tidak lagi terdaftar di kelas ini. Riwayat pendaftaran tetap
                          tersimpan dan santri dapat didaftarkan kembali kapan saja.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction
                          disabled={remove.isPending}
                          onClick={() => {
                            remove.mutate(entry.studentId);
                          }}
                        >
                          Keluarkan
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </li>
            ))}
          </ul>
        )}

        {canManage && (
          <div className="flex flex-col gap-2 border-t border-border pt-4 sm:flex-row sm:items-end">
            <div className="flex flex-1 flex-col gap-2">
              <Select value={selectedStudentId} onValueChange={setSelectedStudentId}>
                <SelectTrigger id="enroll-student" aria-label="Pilih santri">
                  <SelectValue placeholder="Pilih santri untuk didaftarkan" />
                </SelectTrigger>
                <SelectContent>
                  {availableStudents.map((student) => (
                    <SelectItem key={student.id} value={student.id}>
                      {student.fullName} ({student.email})
                      {student.guardianName !== null ? ` - Wali: ${student.guardianName}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              disabled={selectedStudentId.length === 0 || enroll.isPending}
              onClick={handleEnroll}
            >
              {enroll.isPending ? 'Mendaftarkan...' : 'Daftarkan'}
            </Button>
          </div>
        )}

        {apiErrorMessage !== null && (
          <Alert variant="destructive">
            <AlertDescription>{apiErrorMessage}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
